import { useState, useMemo } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useAuth } from '../../context/AuthContext';
import { useApiCall } from '../../hooks/useApi';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';
import EmptyState from '../../components/ui/EmptyState';
import PageError from '../../components/ui/PageError';
import Skeleton from '../../components/ui/Skeleton';
import { TH, TR, TD } from '../../components/ui/Table';
import { cleanReportCadence, demoVisibleReports, reportSizeWarning } from '../../utils/mspPolish';
import { normalizeReportStatus, reportArtifactState, reportStatusPresentation } from '../../utils/reportJobs';

const typeLabels = { monthly: 'Monthly', qbr: 'QBR', compliance: 'Monthly Compliance', pentest: 'Pentest', snapshot: 'Snapshot' };
const typeColors = { monthly: C.sky, qbr: C.pur, compliance: C.grn, pentest: C.red, snapshot: C.amb };

function fallbackFormats(type) {
  if (type === 'monthly' || type === 'compliance') return ['docx', 'pptx'];
  if (type === 'qbr') return ['pptx'];
  return [];
}

export default function MspReports() {
  const { data: raw, loading, error } = useApiCall('/reports');
  const { getAccessToken } = useAuth();
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const reports = useMemo(() => (raw ?? []).map(r => {
    const type = (r.reportType ?? r.type ?? 'monthly').toString().toLowerCase().replace(/\d+/g, '');
    const artifact = reportArtifactState(r, fallbackFormats(type));
    return {
      id: r.reportId ?? r.id ?? '',
      clientId: r.clientId ?? '',
      clientName: r.clientName ?? '',
      name: r.title ?? r.name ?? 'Report',
      type,
      gen: r.generatedAt ?? null,
      status: normalizeReportStatus(r.status, artifact.hasFile, artifact.artifactStatus),
      artifactStatus: artifact.artifactStatus,
      artifact: artifact.artifact,
      hasFile: artifact.hasFile,
      availableFormats: artifact.availableFormats,
      error: r.error ?? artifact.artifact?.error ?? null,
      errorMessage: r.errorMessage ?? r.lastErrorMessage ?? r.failureReason ?? r.statusMessage ?? '',
      size: r.sizeBytes ? `${Math.round(r.sizeBytes / 1024)} KB` : null,
      sizeBytes: r.sizeBytes ?? 0,
      demoMetadata: r.demoMetadata ?? null,
    };
  }), [raw]);
  const visibleReports = useMemo(() => cleanReportCadence(demoVisibleReports(reports)), [reports]);

  // Group by client
  const grouped = useMemo(() => {
    const map = {};
    for (const r of visibleReports) {
      const key = r.clientId || 'unknown';
      if (!map[key]) map[key] = { clientName: r.clientName || 'Unknown Client', reports: [] };
      map[key].reports.push(r);
    }
    return Object.values(map);
  }, [visibleReports]);

  const handleDownload = async (report, format) => {
    if (!report.hasFile) {
      setDownloadError('No file available for this report.');
      return;
    }
    if (!report.availableFormats.includes(format)) {
      setDownloadError(`${format?.toUpperCase() || 'That'} export is not available for ${typeLabels[report.type] || report.type}.`);
      return;
    }
    if (!format) {
      setDownloadError('No editable export is available for this report.');
      return;
    }
    const downloadKey = `${report.id}:${format}`;
    setDownloadingId(downloadKey);
    setDownloadError(null);
    try {
      const token = await getAccessToken();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const url = `${baseUrl}/clients/${report.clientId}/reports/${report.id}/${format}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${report.name.replace(/[^a-zA-Z0-9-_ ]/g, '')}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Shell title="MSP Reports" sub={loading ? 'Loading...' : `${visibleReports.length} reports across ${grouped.length} client${grouped.length !== 1 ? 's' : ''}`}>
      {loading && (
        <Card style={{ padding: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <Skeleton width={100} height={12} />
                <Skeleton width={80} height={12} />
                <Skeleton width={120} height={12} />
                <Skeleton width={60} height={12} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {error && <PageError error={error} />}

      {downloadError && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: C.redBg, border: `1px solid ${C.red}33`, color: C.red, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Download failed: {downloadError}</span>
          <button onClick={() => setDownloadError(null)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>{'\u2715'}</button>
        </div>
      )}

      {!loading && !error && visibleReports.length === 0 && (
        <EmptyState title="No reports generated yet" sub="Reports will appear here once scans complete and reports are generated for your clients." />
      )}

      {!loading && !error && grouped.map(group => (
        <Card key={group.clientName} style={{ marginBottom: 14 }}>
          <CardHeader title={group.clientName} action={
            <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{group.reports.length} report{group.reports.length !== 1 ? 's' : ''}</span>
          } />
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <TH cols={['Report', 'Type', 'Generated', 'Size', 'Status', '']} />
            <tbody>
              {group.reports.map(r => (
                <TR key={r.id}>
                  <TD>
                    <div style={{ fontSize: 12, color: C.t1, fontWeight: 500 }}>{r.name}</div>
                  </TD>
                  <TD>
                    <Tag label={typeLabels[r.type] || r.type} color={typeColors[r.type] || C.sky} />
                  </TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, whiteSpace: 'nowrap' }} title={r.gen ? exactTime(r.gen) : ''}>
                    {r.gen ? relativeTime(r.gen) : '\u2014'}
                  </TD>
                  <TD style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
                    <div>{r.size || '\u2014'}</div>
                    {reportSizeWarning(r) && <div style={{ color: C.amb, fontSize: 9, marginTop: 2 }}>{reportSizeWarning(r)}</div>}
                  </TD>
                  <TD>
                    {(() => {
                      const status = reportStatusPresentation(r);
                      const color = status.tone === 'success' ? C.grn : status.tone === 'danger' ? C.red : status.tone === 'warning' ? C.amb : C.t2;
                      return <Tag label={status.label} color={color} />;
                    })()}
                  </TD>
                  <TD>
                    {r.hasFile && r.availableFormats.length > 0 ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.availableFormats.map((format, idx) => (
                          <Button key={format} variant={idx === 0 ? 'pri' : 'sec'} style={{ fontSize: 10 }} onClick={() => handleDownload(r, format)} disabled={downloadingId === `${r.id}:${format}`}>
                            {downloadingId === `${r.id}:${format}` ? 'Downloading...' : format.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>No editable export</span>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </Shell>
  );
}
